// Moteur de conversion cote / probabilité du cluster « Probabilités & cotes ».
//
// Périmètre volontairement isolé (issue #1834) : ces fonctions servent des outils
// pédagogiques anonymes alimentés par la saisie utilisateur. Elles ne consomment
// et ne reproduisent AUCUN service de scoring de production (Scoring::DevigKernel,
// MarketProbabilities, OddsSnapshot) — modifier l'un ne doit jamais impacter l'autre.
//
// Conventions :
// - une probabilité est une fraction de 0 à 1 (0,5 = 50 %), jamais un pourcentage ;
// - aucun arrondi intermédiaire : le calcul reste en précision flottante native,
//   `roundTo` n'est appliqué qu'à l'affichage (2 décimales par défaut) ;
// - une entrée invalide renvoie `null` plutôt que NaN, pour que l'appelant décide
//   quoi afficher ;
// - les avertissements sont renvoyés sous forme de code (`ODDS_TOO_LOW`…), jamais
//   de texte : la traduction reste côté Rails (i18n), pas dans le bundle JS.
//
// Usage :
//   import { decimalToProbability, fairOdds } from "../lib/odds_math"

// Méthode de retrait de marge appliquée par `normalizeProbabilities` / `fairOdds`.
// Normalisation proportionnelle : chaque probabilité implicite est divisée par la
// somme du marché. Choix explicite et assumé — les méthodes alternatives (power,
// logarithmique, Shin, correction du biais favori/outsider) répartissent la marge
// différemment et donneraient d'autres cotes sans marge.
export const DEVIG_METHOD = "proportional"

// Une cote décimale strictement supérieure à 1 est la seule qui rapporte plus que
// la mise. 1,00 (ou moins) ne correspond à aucun pari réel : on le signale au lieu
// de renvoyer un résultat qui aurait l'air valide.
export const MIN_DECIMAL_ODDS = 1

export const WARNINGS = {
  ODDS_TOO_LOW: "odds_too_low",
  PROBABILITY_OUT_OF_RANGE: "probability_out_of_range",
  INVALID_NUMBER: "invalid_number",
  NEGATIVE_EDGE: "negative_edge"
}

// ---------------------------------------------------------------------------
// Parsing des saisies
// ---------------------------------------------------------------------------

// Accepte la virgule décimale française et la notation anglo-saxonne, les espaces
// (y compris insécables des claviers/copier-coller), le symbole %, un signe + ou -
// en tête (formats américains). Renvoie `null` si rien d'exploitable.
export function parseNumeric(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (value == null) return null

  const cleaned = String(value)
    .replace(/[\s  ]/g, "")
    .replace(/%/g, "")
    .replace(",", ".")

  if (cleaned === "" || cleaned === "+" || cleaned === "-") return null
  if (!/^[+-]?\d*\.?\d+$/.test(cleaned)) return null

  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

// Cote décimale saisie : `2,20` / `2.20`. Renvoie `null` si la valeur n'est pas un
// nombre ; une cote ≤ 1 reste renvoyée telle quelle (l'appelant décide d'afficher
// l'avertissement `ODDS_TOO_LOW` plutôt que de masquer la saisie).
export function parseDecimalOdds(value) {
  const parsed = parseNumeric(value)
  return parsed === null || parsed <= 0 ? null : parsed
}

// Probabilité saisie en pourcentage (`40`, `40 %`, `40,5`) → fraction 0-1.
export function parsePercentage(value) {
  const parsed = parseNumeric(value)
  return parsed === null ? null : parsed / 100
}

// Cote fractionnelle : `3/2`, `7/4`, `1/1`. Un entier seul (`3`) est lu comme `3/1`.
export function parseFractional(value) {
  if (value == null) return null

  const cleaned = String(value).replace(/[\s  ]/g, "")
  const match = cleaned.match(/^(\d+(?:[.,]\d+)?)(?:\/(\d+(?:[.,]\d+)?))?$/)
  if (!match) return null

  const numerator = parseNumeric(match[1])
  const denominator = match[2] === undefined ? 1 : parseNumeric(match[2])
  if (numerator === null || denominator === null || denominator === 0) return null

  return { numerator, denominator }
}

// ---------------------------------------------------------------------------
// Conversions élémentaires
// ---------------------------------------------------------------------------

// decimal_to_probability : probabilité implicite d'une cote décimale (fraction 0-1).
export function decimalToProbability(odds) {
  const value = parseDecimalOdds(odds)
  return value === null ? null : 1 / value
}

// probability_to_decimal : cote décimale correspondant à une probabilité.
export function probabilityToDecimal(probability) {
  const value = parseNumeric(probability)
  if (value === null || value <= 0 || value > 1) return null
  return 1 / value
}

// decimal_to_fractional : approximation de la cote décimale en fraction irréductible.
// `2,50` → 3/2. La recherche se limite à des dénominateurs usuels (jusqu'à 100) :
// au-delà, la fraction cesse d'être lisible pour un lecteur humain.
export function decimalToFractional(odds, { maxDenominator = 100 } = {}) {
  const value = parseDecimalOdds(odds)
  if (value === null || value <= MIN_DECIMAL_ODDS) return null

  const target = value - 1
  let best = { numerator: 1, denominator: 1, error: Math.abs(target - 1) }

  for (let denominator = 1; denominator <= maxDenominator; denominator++) {
    const numerator = Math.round(target * denominator)
    if (numerator < 1) continue

    const error = Math.abs(target - numerator / denominator)
    if (error < best.error - 1e-12) best = { numerator, denominator, error }
    if (best.error < 1e-12) break
  }

  const divisor = greatestCommonDivisor(best.numerator, best.denominator)
  const numerator = best.numerator / divisor
  const denominator = best.denominator / divisor

  return { numerator, denominator, label: `${numerator}/${denominator}` }
}

// fractional_to_decimal : `3/2` → 2,50. Accepte une chaîne ou {numerator, denominator}.
export function fractionalToDecimal(value, denominator = undefined) {
  const fraction =
    typeof value === "object" && value !== null && denominator === undefined
      ? value
      : denominator === undefined
        ? parseFractional(value)
        : { numerator: parseNumeric(value), denominator: parseNumeric(denominator) }

  if (!fraction) return null
  const { numerator, denominator: den } = fraction
  if (numerator == null || den == null || den === 0) return null

  return numerator / den + 1
}

// decimal_to_american : cote américaine (moneyline). Au-dessus de 2,00 le format
// est positif (gain net pour 100 misés), en dessous il est négatif (mise nécessaire
// pour gagner 100).
export function decimalToAmerican(odds) {
  const value = parseDecimalOdds(odds)
  if (value === null || value <= MIN_DECIMAL_ODDS) return null

  return value >= 2 ? (value - 1) * 100 : -100 / (value - 1)
}

// american_to_decimal : `+150` → 2,50 ; `-200` → 1,50.
export function americanToDecimal(american) {
  const value = parseNumeric(american)
  if (value === null || value === 0) return null
  if (value > 0 && value < 100) return null
  if (value < 0 && value > -100) return null

  return value > 0 ? value / 100 + 1 : 100 / Math.abs(value) + 1
}

// ---------------------------------------------------------------------------
// Lecture d'un marché complet
// ---------------------------------------------------------------------------

// Probabilités implicites d'une liste de cotes. Les entrées invalides sont ignorées
// pour que le reste du marché reste calculable pendant la saisie.
export function impliedProbabilities(oddsList) {
  return toArray(oddsList)
    .map((odds) => decimalToProbability(odds))
    .filter((probability) => probability !== null)
}

// book_percentage : somme des probabilités implicites (1,0556 = 105,56 %).
export function bookPercentage(oddsList) {
  const probabilities = impliedProbabilities(oddsList)
  if (probabilities.length === 0) return null

  return probabilities.reduce((sum, probability) => sum + probability, 0)
}

// overround : part du book percentage située au-dessus de 100 % (0,0556 = 5,56 points).
export function overround(oddsList) {
  const book = bookPercentage(oddsList)
  return book === null ? null : book - 1
}

// theoretical_payout : taux de retour théorique du marché (1 ÷ book percentage).
export function theoreticalPayout(oddsList) {
  const book = bookPercentage(oddsList)
  return book === null || book === 0 ? null : 1 / book
}

// theoretical_margin : part théoriquement retenue sur les mises (1 − taux de retour).
// Distincte de l'overround : sur un book à 105,56 %, l'overround vaut 5,56 points
// mais la marge théorique 5,26 % — et aucune des deux n'est un bénéfice constaté.
export function theoreticalMargin(oddsList) {
  const payout = theoreticalPayout(oddsList)
  return payout === null ? null : 1 - payout
}

// normalize_probabilities : retrait de marge par normalisation proportionnelle.
// Chaque probabilité est divisée par la somme du marché ; le total vaut 1.
export function normalizeProbabilities(probabilities, { method = DEVIG_METHOD } = {}) {
  if (method !== DEVIG_METHOD) {
    throw new Error(`Unsupported devig method: ${method} (only "${DEVIG_METHOD}" is implemented)`)
  }

  const values = toArray(probabilities)
    .map((probability) => parseNumeric(probability))
    .filter((probability) => probability !== null && probability > 0)

  const total = values.reduce((sum, probability) => sum + probability, 0)
  if (values.length === 0 || total === 0) return []

  return values.map((probability) => probability / total)
}

// fair_odds : cotes sans marge estimées d'un marché (probabilités normalisées inversées).
// « Estimation », jamais « vraie cote » : le résultat dépend des prix observés et de
// la méthode de répartition retenue.
export function fairOdds(oddsList, { method = DEVIG_METHOD } = {}) {
  const normalized = normalizeProbabilities(impliedProbabilities(oddsList), { method })
  return normalized.map((probability) => 1 / probability)
}

// Cote juste déduite d'une probabilité estimée (1 ÷ probabilité) — le pendant à une
// seule issue de `fairOdds`.
export function fairOddsFromProbability(probability) {
  return probabilityToDecimal(probability)
}

// ---------------------------------------------------------------------------
// Valeur théorique et cote de clôture
// ---------------------------------------------------------------------------

// Avantage théorique d'un pari : probabilité estimée × cote − 1 (0,10 = +10 %).
// Ne dit rien de la justesse de la probabilité estimée, qui vient toujours d'ailleurs
// que de la cote elle-même.
export function edge({ odds, probability }) {
  const decimalOdds = parseDecimalOdds(odds)
  const estimated = parseNumeric(probability)
  if (decimalOdds === null || estimated === null || estimated <= 0 || estimated > 1) return null

  return decimalOdds * estimated - 1
}

// expected_value : espérance théorique en unités de mise (mise 10 € et edge +10 % → +1 €).
export function expectedValue({ odds, probability, stake = 1 }) {
  const theoreticalEdge = edge({ odds, probability })
  const amount = parseNumeric(stake)
  if (theoreticalEdge === null || amount === null) return null

  return theoreticalEdge * amount
}

// kelly_fraction : fraction de bankroll qu'implique le critère de Kelly —
// f* = (b·p − q) / b, avec b = cote décimale − 1, q = 1 − p. Équivalent à
// edge / b (le calcul retenu par le calculateur `:::calc kelly`, cf. #2158).
// Peut être négative ou nulle : aucun edge ne veut dire aucune fraction à
// engager, ce n'est pas une erreur de saisie. L'appelant (contrôleur Stimulus)
// décide de masquer une mise négative plutôt que de l'afficher.
export function kellyFraction({ odds, probability }) {
  const decimalOdds = parseDecimalOdds(odds)
  const estimated = parseNumeric(probability)
  if (decimalOdds === null || estimated === null || estimated <= 0 || estimated > 1) return null

  const b = decimalOdds - 1
  if (b <= 0) return null

  const theoreticalEdge = edge({ odds: decimalOdds, probability: estimated })
  return theoreticalEdge === null ? null : theoreticalEdge / b
}

// closing_line_value : écart entre la cote obtenue et la cote de clôture.
// - `simple` : cote obtenue ÷ cote de clôture − 1 ;
// - `adjusted` : même écart mesuré en probabilités sans marge du marché de clôture,
//   ce qui neutralise la marge (ou la commission) intégrée au prix final ;
// - `absolute` : différence brute de cotes.
// Une CLV positive décrit un prix pris au-dessus du prix final. Ce n'est pas une
// garantie de bénéfice.
export function closingLineValue({ obtainedOdds, closingOdds, marginPoints = 0 }) {
  const obtained = parseDecimalOdds(obtainedOdds)
  const closing = parseDecimalOdds(closingOdds)
  if (obtained === null || closing === null) return null

  const obtainedProbability = 1 / obtained
  const closingProbability = 1 / closing

  // Marge (ou commission) déclarée sur le marché de clôture, en points de pourcentage.
  // On la retire proportionnellement de la probabilité de clôture avant comparaison :
  // sans cette correction, la CLV d'un marché chargé en marge est mécaniquement
  // surestimée.
  const margin = parseNumeric(marginPoints) ?? 0
  const divisor = 1 + margin / 100
  const closingFairProbability = divisor === 0 ? closingProbability : closingProbability / divisor
  const closingFairOdds = closingFairProbability === 0 ? null : 1 / closingFairProbability

  return {
    obtainedProbability,
    closingProbability,
    closingFairProbability,
    closingFairOdds,
    absolute: obtained - closing,
    simple: obtained / closing - 1,
    adjusted: closingFairOdds === null ? null : obtained / closingFairOdds - 1
  }
}

// roi : profit net et ROI observés sur un ensemble de paris déjà réglés (page
// /roi-paris-sportifs). `null` si le total misé n'est pas strictement positif —
// un ROI n'a pas de sens sans dénominateur.
export function roi({ totalStakes, totalReturn }) {
  const stakes = parseNumeric(totalStakes)
  const returns = parseNumeric(totalReturn)
  if (stakes === null || returns === null || stakes <= 0) return null

  const netProfit = returns - stakes
  return { netProfit, roi: netProfit / stakes }
}

// ---------------------------------------------------------------------------
// Retour d'une mise
// ---------------------------------------------------------------------------

// Retour brut (mise incluse) et gain net d'une mise gagnante.
export function stakeReturn({ odds, stake = 1 }) {
  const decimalOdds = parseDecimalOdds(odds)
  const amount = parseNumeric(stake)
  if (decimalOdds === null || amount === null) return null

  const gross = decimalOdds * amount
  return { gross, net: gross - amount }
}

// ---------------------------------------------------------------------------
// Valeur d'un freebet (#2726)
// ---------------------------------------------------------------------------

// freebet_value : valeur espérée d'un freebet « mise non rendue ». S'il gagne, il
// ne rapporte que le gain net, montant × (cote − 1) ; la mise offerte n'est jamais
// créditée. Sa valeur espérée vaut donc probabilité sans marge × gain net.
//
// La probabilité sans marge vient de l'une des deux sources :
// - les cotes des autres issues du marché (`otherOdds`), normalisées avec la cote
//   jouée : c'est alors le marché lui-même qui donne son overround ;
// - à défaut, un overround déclaré en points (`overroundPoints`, 6 pour un book
//   percentage de 106 %, jamais négatif), retiré proportionnellement de la
//   probabilité implicite.
//
// Modèle documenté dans Whelan, « The Economics of Free Bets » (2026). Une valeur
// espérée n'est jamais un gain : elle décrit une moyenne sur un grand nombre de
// paris, alors qu'un freebet donné rapporte soit le gain net, soit rien.
export function freebetValue({ amount, odds, overroundPoints = null, otherOdds = [] }) {
  const faceValue = parseNumeric(amount)
  const decimalOdds = parseDecimalOdds(odds)
  if (faceValue === null || faceValue <= 0) return null
  if (decimalOdds === null || decimalOdds <= MIN_DECIMAL_ODDS) return null

  const others = toArray(otherOdds).map((value) => parseDecimalOdds(value))
  if (others.some((value) => value === null || value <= MIN_DECIMAL_ODDS)) return null

  const impliedProbability = 1 / decimalOdds
  let fairProbability
  let marketOverround

  if (others.length > 0) {
    const market = [ decimalOdds, ...others ]
    fairProbability = normalizeProbabilities(market.map((value) => 1 / value))[0]
    marketOverround = overround(market)
  } else {
    // Un overround négatif décrirait des prix plus généreux que la probabilité
    // réelle : aucun bookmaker ne cote ainsi un marché entier.
    const points = parseNumeric(overroundPoints)
    if (points === null || points < 0) return null

    marketOverround = points / 100
    fairProbability = impliedProbability / (1 + marketOverround)
  }

  if (fairProbability > 1) return null

  const winReturn = faceValue * (decimalOdds - 1)
  const expected = fairProbability * winReturn

  return {
    winReturn,
    impliedProbability,
    fairProbability,
    overround: marketOverround,
    expectedValue: expected,
    valueShare: expected / faceValue
  }
}

// ---------------------------------------------------------------------------
// Avertissements et arrondis d'affichage
// ---------------------------------------------------------------------------

// Codes d'avertissement d'une cote saisie. À traduire côté Rails via i18n.
export function oddsWarning(odds) {
  const value = parseNumeric(odds)
  if (value === null) return WARNINGS.INVALID_NUMBER
  if (value <= MIN_DECIMAL_ODDS) return WARNINGS.ODDS_TOO_LOW
  return null
}

export function probabilityWarning(probability) {
  const value = parseNumeric(probability)
  if (value === null) return WARNINGS.INVALID_NUMBER
  if (value <= 0 || value > 1) return WARNINGS.PROBABILITY_OUT_OF_RANGE
  return null
}

// Arrondi d'affichage uniquement. Le calcul en amont reste en pleine précision.
export function roundTo(value, digits = 2) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null

  const factor = 10 ** digits
  return Math.round((value + Number.EPSILON * Math.sign(value || 1)) * factor) / factor
}

function greatestCommonDivisor(a, b) {
  let x = Math.abs(Math.round(a))
  let y = Math.abs(Math.round(b))
  while (y) {
    const remainder = x % y
    x = y
    y = remainder
  }
  return x || 1
}

function toArray(value) {
  if (Array.isArray(value)) return value
  return value == null ? [] : [ value ]
}
